import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import FinancialProductRulesEditDetailsCommissionsTable from './FinancialProductRulesEditDetailsCommissionsTable';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation, useSubscription } from 'hooks/apollo';
import { getInt } from 'utils/functions';
import { useRouteMatch } from 'react-router-dom';
import { useToast } from 'hooks/toasts/useToast';
import { REMOVE_FINANCIAL_PRODUCT_RULE_COMMISSION } from '../../../mutations';
import { FINANCIAL_PRODUCT_RULE_COMMISSION_REMOVED, FINANCIAL_PRODUCT_RULE_COMMISSION_UPDATED } from '../../../subscriptions';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductRuleCommissionsValidator } from '../../../validators';
import FinancialProductRulesEditDetailsCommissionsModalComponent from './FinancialProductRulesEditDetailsCommissionsModalComponent';
import { financialProductRuleCommissionModalVar } from './variables';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';

const FinancialProductRulesEditDetailsCommissionsContainer = ({ canWrite, commissionList, isSaved }) => {
  const addToast = useToast();
  const [t] = useTranslation();
  const match = useRouteMatch();
  const ruleId = match.params.ruleId && getInt(match.params.ruleId);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [financialProductRuleCommissionValidation, validateFinancialProductRuleCommission] = useDirtyFieldValidation(
    financialProductRuleCommissionsValidator
  );

  // #region effect
  useEffect(() => {
    resetLens(
      commissionList?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        if (elementLocal.commissionDefaultPercent) {
          elementLocal.usesPercent = true;
          elementLocal.usesValue = false;
        }
        if (elementLocal.commissionDefaultValue) {
          elementLocal.usesValue = true;
          elementLocal.usesPercent = false;
        }
        return elementLocal;
      }) || []
    );
  }, [resetLens, commissionList]);

  useEffect(() => {
    validateFinancialProductRuleCommission(lens |> get, dirtyInfo);
  }, [dirtyInfo, lens, validateFinancialProductRuleCommission]);

  // #endregion effect

  useSubscription(FINANCIAL_PRODUCT_RULE_COMMISSION_UPDATED, {
    onData: ({ data: { data }, client }) => {
      financialProductRuleCommissionModalVar({ open: false });
      const lensLocal = [...(lens |> get)];
      addToast(t('FinancialProduct.Details.Rules.Details.Commissions.FinancialProductRuleCommissionSaved'), 'success');
      lensLocal.forEach((element, index) => {
        if (index === data.financialProductRuleCommissionUpdated.orderNo) {
          element.financialProductRuleCommissionId = data.financialProductRuleCommissionUpdated.commissionId;
          element.isInEditing = false;
          element.isSaving = false;
        }
      });
      resetLens(lensLocal);
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });
      client.cache.gc();
    }
  });

  const [deleteFinancialProductRuleCommission] = useMutation(REMOVE_FINANCIAL_PRODUCT_RULE_COMMISSION);

  useSubscription(FINANCIAL_PRODUCT_RULE_COMMISSION_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Rules.Details.Commissions.FinancialProductRuleCommissionRemoved'), 'success');
      const lensLocal = [...(lens |> get)];
      const objTL = lensLocal.filter(el => el.financialProductRuleCommissionId !== data.financialProductRuleCommissionDeleted.commissionId);
      resetLens(objTL);
      client.cache.evict({
        id: `FinancialProductRuleEditItem:{"financialProductRuleId":${ruleId}}`
      });
    }
  });

  // #endregion alter

  // #region handlers

  const handleDelete = commissionId => () => {
    if (window.confirm(t('FinancialProduct.Details.Rules.Details.Commissions.DeleteFinancialProductRuleCommission'))) {
      if (commissionId) {
        deleteFinancialProductRuleCommission({
          variables: {
            input: { commissionId }
          }
        });
      } else {
        resetLens(commissionList);
      }
    }
  };

  //  #endregion handlers

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <>
      <FinancialProductRulesEditDetailsCommissionsModalComponent />
      <FinancialProductRulesEditDetailsCommissionsTable
        canWrite={canWrite}
        commissionList={lens}
        isSaved={isSaved}
        onDelete={handleDelete}
        financialProductRuleCommissionValidation={financialProductRuleCommissionValidation}
      />
    </>
  );
};

FinancialProductRulesEditDetailsCommissionsContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  commissionList: PropTypes.array.isRequired,
  isSaved: PropTypes.bool.isRequired
};

export default FinancialProductRulesEditDetailsCommissionsContainer;
